import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { searchUserDirectory } from '../lib/api';
import { errorMessage, isValidEmail } from '../lib/utils';
import type { DirectoryUser } from '../types';
import { useToast } from '../context/ToastContext';

function normalizeValue(value: string) {
  return value.trim().replace(/^,+|,+$/g, '').trim();
}

export default function ExpectedAttendeePicker({ values, onChange, autoValues = [] }: {
  values: string[];
  onChange: (values: string[]) => void;
  autoValues?: string[];
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const { showToast } = useToast();

  const displayedValues = useMemo(() => Array.from(new Map([...values, ...autoValues].map((value) => [value.toLowerCase(), value])).values()), [autoValues, values]);
  const autoValueSet = useMemo(() => new Set(autoValues.map((value) => value.toLowerCase())), [autoValues]);
  const normalizedValues = useMemo(() => new Set(displayedValues.map((value) => value.toLowerCase())), [displayedValues]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const matches = await searchUserDirectory(q);
        if (!cancelled) setResults(matches.filter((item) => !normalizedValues.has(item.email.toLowerCase())));
      } catch (error) {
        if (!cancelled) showToast(errorMessage(error), 'error');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 260);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, normalizedValues, showToast]);

  const addValue = (raw: string) => {
    const value = normalizeValue(raw);
    if (!value) return;
    if (value.length > 200) {
      showToast('Setiap ahli dijemput dihadkan kepada 200 aksara.', 'error');
      return;
    }
    if (!normalizedValues.has(value.toLowerCase())) onChange([...values, value]);
    setQuery('');
    setResults([]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      if (results.length === 1 && query.trim().length >= 3) addValue(results[0].email);
      else addValue(query);
    }
    if (event.key === 'Backspace' && !query && values.length) onChange(values.slice(0, -1));
  };

  return <div className="attendee-picker">
    {displayedValues.length > 0 && <div className="attendee-chips">{displayedValues.map((value) => (
      <span className="attendee-chip" key={value.toLowerCase()}>
        <span>{autoValueSet.has(value.toLowerCase()) ? <><em>Co-Host</em> · {value}</> : value}</span>
        {autoValueSet.has(value.toLowerCase())
          ? <small className="attendee-chip-lock">Automatik</small>
          : <button type="button" aria-label={`Keluarkan ${value}`} onClick={() => onChange(values.filter((item) => item !== value))}>×</button>}
      </span>
    ))}</div>}
    <div className="attendee-search-wrap">
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (query.trim() && results.length === 0 && (isValidEmail(query) || query.trim().length >= 2)) addValue(query); }}
        placeholder="Cari atau masukkan nama atau e-mel…"
        autoComplete="off"
      />
      {query.trim().length >= 3 && <div className="directory-results">
        {searching ? <div className="directory-message">Sedang mencari pengguna berdaftar…</div> : results.length ? results.map((user) => (
          <button type="button" className="directory-result" key={user.email} onMouseDown={(event) => event.preventDefault()} onClick={() => addValue(user.email)}>
            <strong>{user.full_name}</strong>
            <span>{user.email}</span>
            <small>{user.department_org}</small>
          </button>
        )) : <div className="directory-message">Tiada pengguna yang sepadan. Tekan Enter untuk menambah “{query.trim()}” sebagai tetamu.</div>}
      </div>}
    </div>
    <small>Pegawai berdaftar akan dipaparkan selepas 3 aksara. Untuk menambah tetamu yang belum berdaftar, masukkan nama atau e-mel dan tekan Enter. Kehadiran hanya direkodkan selepas <em>check-in</em>.</small>
  </div>;
}
